/**
 * `/metal-resources/metal-market-intelligence/<slug>`: one page file for both kinds of Metal
 * Market Intelligence ("The Gauge") URL. A slug that names a Contentful `blogCategory` renders
 * the category page (`components/Blog/Category.tsx`). Any other slug is a `blog` article
 * (`components/Blog/Blog.tsx`). Both are built at build time from Contentful with
 * `@ryerson/frontend.contentful` → `Crawl`.
 *
 * Build:
 * - `getStaticPaths` lists every `blogCategory` slug plus every `blog` slug, read from the
 *   `en-US` locale only, with `fallback: false`, so any other slug is a 404. On a build whose
 *   `NEXT_PUBLIC_COMPANY` isn't `ryerson` it lists no paths. That is the only brand gate:
 *   `getStaticProps` has no company check of its own.
 * - `getStaticProps` runs once per slug. Every run fetches the category slugs again and every
 *   `blog` entry in all three locales (`vanillaEntries`), then fetches either the one category or
 *   the one article, with rich text resolved.
 * - Category: its `blog` list is every article that, in the same locale, carries a category
 *   whose title equals this category's title. Newest `publishDate` first.
 * - Article: `contentfulBlogEntries` is the six newest articles per locale, for the "Popular
 *   Content" sidebar (`components/Blog/PopularContent.tsx`) and the insights section.
 * - Neither list is filtered by brand: `vanillaEntries` reads `company` but nothing compares it,
 *   and every `blog` slug gets a page. The landing page's list is filtered
 *   (`src/contentful/blog/helpers/index.tsx` → `transformContentfulBlogEntriesData`). So an entry
 *   whose first `company` isn't Ryerson is missing from the landing, but has a page here and
 *   shows on category pages and in Popular Content. Today the space holds only Ryerson `blog`
 *   entries, but entries for other brands are planned, and the first one published goes live
 *   on ryerson.com this way. The two unfiltered reads are `getBlogSlugs` and `vanillaEntries`.
 * - The `getStaticProps` return isn't type-checked against `@contentful/blog/types`. The `Crawl`
 *   results are cast to the local interfaces below, and the page component declares its props as
 *   `BlogCategoryPageProps & BlogArticlePageProps` (see the header of
 *   `src/contentful/blog/types/index.ts`).
 *
 * Rules a change must keep:
 * - Category and article slugs share one URL space. `getStaticProps` checks the category list
 *   first, so a slug in both lists renders the category and the article has no page of its own.
 * - `Crawl` pages through the whole collection. It makes up to three attempts per request, six
 *   seconds apart, and then throws, which fails the build (web-architecture ADR-0033).
 * - `Crawl` returns a one-element result as the element itself, not as an array. The
 *   single-entry lookups (`translatedCategory`, `contentfulBlogContent`) rely on that. The list
 *   reads (`getCategorySlugs`, `getBlogSlugs`, `vanillaEntries`) call array methods on the
 *   result, so they throw if a collection holds exactly one entry.
 * - Links to these pages are built as `"/metal-resources/metal-market-intelligence/" + slug` in
 *   `components/Blog/*`, `components/MetalResources/MetalMarketIntelligence/`,
 *   `components/Shared/MetalInsights/MetalInsightsAndResourcesRefactor.tsx` and
 *   `components/WhyRyerson/SuccessStories/`. Moving the route means changing all of them.
 *
 * Related: `./index.tsx` (the landing at `/metal-resources/metal-market-intelligence`),
 * `components/Blog/BlogMetaHead.tsx` and `BlogCategoryMetaHead.tsx` (each kind's SEO tags),
 * `components/Blog/BlogStaticContent.tsx` (the article sidebar copy).
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import BlogCategoryPage from "@components/Blog/Category";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import { BlogArticlePageProps, BlogCategoryPageProps } from "@contentful/blog/types";
import Blog from "@components/Blog/Blog";
import { blogStaticeContent } from "@components/Blog/BlogStaticContent";
import { Crawl } from "@ryerson/frontend.contentful";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import BlogMetaHead from "@components/Blog/BlogMetaHead";
import BlogCategoryMetaHead from "@components/Blog/BlogCategoryMetaHead";

// Build-time slug lookups, then the local shapes of the `Crawl` results, then the page.

/**
 * The `getStaticProps` context, narrowed to `params.slug`. `allSlugStrings` is never set or
 * read.
 */
type PathParams = {
	params: { slug: string };
	allSlugStrings: string[];
};

/**
 * Every `blogCategory` slug, from the `en-US` locale. Fetched by both `getStaticPaths` and every
 * `getStaticProps` run.
 */
const getCategorySlugs = async () => {
	const categoryData = (await new Crawl().get("blogCategory", {
		resolveRichText: false,
		depth: 1,
		locales: ["en-US"],
		include: ["slug"],
	})) as LanguageContent<{ slug: string }[]>;
	return categoryData.en.map((obj: { slug: string }) => obj.slug);
};

/** Every `blog` slug, from the `en-US` locale, for both brands. */
const getBlogSlugs = async () => {
	const blogData = (await new Crawl().get("blog", {
		resolveRichText: false,
		depth: 1,
		locales: ["en-US"],
		include: ["slug"],
	})) as LanguageContent<{ slug: string }[]>;
	return blogData.en.map((obj: { slug: string }) => obj.slug);
};

/**
 * One path per category slug plus one per article slug, Ryerson build only. `fallback: false`:
 * a slug that isn't in Contentful at build time is a 404 until the next build.
 */
export const getStaticPaths = async () => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { paths: [], fallback: false }; // skips static generation for other companies
	}

	let categorySlugs = await getCategorySlugs();
	let blogSlugs = await getBlogSlugs();
	//combine slugs from blogs themselves and blog categories since each can exist at /metal-market-intelligence/<slug>
	let paths = categorySlugs.concat(blogSlugs);
	return {
		paths: paths.map((slug: string) => ({
			params: { slug },
		})),
		fallback: false,
	};
};

/** One full article as `Crawl` returns it for the article page: rich-text `body` resolved. */
interface ContentfulBlogContent {
	body: any;
	categories: {
		categoryColor: string;
		title: string;
	}[];
	company: ("Ryerson" | "Southern Tool Steel")[];
	heroImage: {
		file: {
			url: string;
		};
	};
	heroVideo: string;
	publishDate: string;
	seoDescription: string;
	seoImage: {
		file: {
			url: string;
		};
	};
	seoTitle: string;
	slug: string;
	subTitle: string;
	title: string;
}

/** The list fields of an article: what category pages and the Popular Content sidebar show. */
interface ContentfulBlogEntry {
	categories: {
		categoryColor: string;
		title: string;
	}[];
	company: ("Ryerson" | "Southern Tool Steel")[];
	heroImage: {
		file: {
			url: string;
		};
	};
	publishDate: string;
	slug: string;
	title: string;
	seoDescription: string;
}

/**
 * One category as `Crawl` returns it, plus `blog`, which `getStaticProps` fills with the
 * category's articles. `blurb1` and `blurb2` are plain strings here, although
 * `@contentful/blog/types` → `BlogCategory` types them as nested objects.
 */
interface ContentfulBlogCategory {
	blurb1: string;
	blurb2: string;
	calloutImage: {
		description: string;
		file: {
			url: string;
		};
		title: string;
	};
	calloutText: any;
	categoryColor: string;
	heroImage: {
		file: {
			url: string;
		};
	};
	listPageImage: {
		file: {
			url: string;
		};
	};
	ordinal: number;
	relatedCategories: {
		categoryColor: string;
		listPageImage: {
			file: {
				url: string;
			};
		};
		slug: string;
		title: string;
		verticalListPageImage: {
			file: {
				url: string;
			};
		};
	}[];
	seoDescription: string;
	seoTitle: string;
	slug: string;
	title: string;
	videoId: string;
	blog: ContentfulBlogEntry[];
}
/**
 * Builds one slug's props. Exactly one branch is filled and the other props are `null`:
 * `translatedCategory` for a category slug, or `contentfulBlogContent` plus
 * `contentfulBlogEntries` for an article slug. Header and footer props come from
 * `allContentfulHeaderCalls`. Any Contentful failure throws and fails the build.
 */
export const getStaticProps = async ({ params }: PathParams) => {
	// Check if the slug is a category page
	let categorySlugs = await getCategorySlugs();
	const { props: headerProps } = await allContentfulHeaderCalls();

	// Every article of both brands in all three locales, list fields only. Re-fetched for each
	// slug.
	const vanillaEntries = (await new Crawl().get("blog", {
		resolveRichText: false,
		depth: 3,
		include: [
			"categories",
			"categories.categoryColor",
			"categories.title",
			"company",
			"heroImage",
			"heroImage.file",
			"heroImage.file.url",
			"publishDate",
			"slug",
			"title",
			"seoDescription",
		],
	})) as LanguageContent<ContentfulBlogEntry[]>;
	if (categorySlugs.includes(params.slug)) {
		//this is a category page
		const translatedCategory = (await new Crawl().get("blogCategory", {
			filters: {
				"fields.slug": params.slug,
			},
			resolveRichText: true,
			depth: 4,
			include: [
				"blurb1",
				"blurb2",
				"calloutImage",
				"calloutImage.description",
				"calloutImage.file",
				"calloutImage.file.url",
				"calloutImage.title",
				"calloutText",
				"categoryColor",
				"heroImage",
				"heroImage.file",
				"heroImage.file.url",
				"listPageImage.file",
				"listPageImage.file.url",
				"ordinal",
				"relatedCategories",
				"relatedCategories.categoryColor",
				"relatedCategories.listPageImage.file",
				"relatedCategories.listPageImage.file.url",
				"relatedCategories.slug",
				"relatedCategories.title",
				"relatedCategories.verticalListPageImage",
				"relatedCategories.verticalListPageImage.file",
				"relatedCategories.verticalListPageImage.file.url",
				"seoDescription",
				"seoTitle",
				"slug",
				"title",
				"videoId",
			],
		})) as LanguageContent<ContentfulBlogCategory>;
		//get blogs related to this category
		// Matched on the category title within each locale, not on the entry ID.
		translatedCategory.en.blog = vanillaEntries.en
			.filter((a: ContentfulBlogEntry) =>
				a.categories?.some((c) => c.title === translatedCategory.en.title)
			)
			.sort(
				(a: ContentfulBlogEntry, b: ContentfulBlogEntry) =>
					new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
			);
		translatedCategory.fr.blog = vanillaEntries.fr
			.filter((a: ContentfulBlogEntry) =>
				a.categories?.some((c) => c.title === translatedCategory.fr.title)
			)
			.sort(
				(a: ContentfulBlogEntry, b: ContentfulBlogEntry) =>
					new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
			);
		translatedCategory.es.blog = vanillaEntries.es
			.filter((a: ContentfulBlogEntry) =>
				a.categories?.some((c) => c.title === translatedCategory.es.title)
			)
			.sort(
				(a: ContentfulBlogEntry, b: ContentfulBlogEntry) =>
					new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
			);
		return {
			props: {
				...headerProps,
				translatedCategory,
				contentfulBlogContent: null,
				contentfulBlogEntries: null,
			},
		};
	} else {
		//this is a blog page
		const contentfulBlogContent = (await new Crawl().get("blog", {
			filters: {
				"fields.slug": params.slug,
			},
			resolveRichText: true,
			depth: 3,
			include: [
				"body",
				"categories",
				"categories.categoryColor",
				"categories.title",
				"company",
				"heroImage",
				"heroImage.file",
				"heroImage.file.url",
				"heroVideo",
				"publishDate",
				"seoDescription",
				"seoImage",
				"seoImage.file",
				"seoImage.file.url",
				"seoTitle",
				"slug",
				"subTitle",
				"title",
			],
		})) as LanguageContent<ContentfulBlogContent>;
		// The six newest articles per locale, current article included; `PopularContent` drops it.
		let contentfulBlogEntries: LanguageContent<ContentfulBlogEntry[]> = {
			en: vanillaEntries.en
				.sort((a: ContentfulBlogEntry, b: ContentfulBlogEntry) => {
					// Sort by publishDate in descending order
					return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
				})
				.slice(0, 6),
			fr: vanillaEntries.fr
				.sort((a: ContentfulBlogEntry, b: ContentfulBlogEntry) => {
					// Sort by publishDate in descending order
					return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
				})
				.slice(0, 6),
			es: vanillaEntries.es
				.sort((a: ContentfulBlogEntry, b: ContentfulBlogEntry) => {
					// Sort by publishDate in descending order
					return new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime();
				})
				.slice(0, 6),
		};
		return {
			props: {
				...headerProps,
				contentfulBlogContent: contentfulBlogContent,
				contentfulBlogEntries: contentfulBlogEntries,
				translatedCategory: null,
			},
		};
	}
};

/**
 * Renders the category page with its meta head when `translatedCategory` is set, otherwise the
 * article with its meta head. The article copy outside Contentful comes from
 * `blogStaticeContent` in the visitor's language.
 */
export default (props: HeaderPageProps & BlogCategoryPageProps & BlogArticlePageProps) => {
	const {
		localization: { language },
	} = useApplication();

	return (
		<HeaderLayout {...getHeaderData(props)}>
			{props.translatedCategory ? (
				<>
					<BlogCategoryMetaHead translatedCategory={props.translatedCategory} />
					<BlogCategoryPage content={props.translatedCategory} />
				</>
			) : (
				props.contentfulBlogContent &&
				props.contentfulBlogEntries && (
					<>
						<BlogMetaHead translatedBlogNode={props.contentfulBlogContent} />
						<Blog
							staticContent={blogStaticeContent[language]}
							contentfulBlogContent={props.contentfulBlogContent}
							contentfulBlogEntries={props.contentfulBlogEntries}
						/>
					</>
				)
			)}
		</HeaderLayout>
	);
};
